import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, languageNames, type SupportedLanguage } from '../../i18n';
import styles from './LanguageSwitcher.module.css';

type Props = {
  /**
   * `bar` is the pill that sits in the nav beside the CTA; `stacked` is the
   * open list the mobile menu uses, where there is room to show every language
   * at once and no space for a popup to hang off.
   */
  variant?: 'bar' | 'stacked';
  onSelect?: () => void;
};

const LanguageSwitcher = ({ variant = 'bar', onSelect }: Props) => {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // `resolvedLanguage` rather than `language`: a visitor arriving as `en-SG`
  // has `language === 'en-SG'`, which matches none of our codes.
  const current = (i18n.resolvedLanguage ?? 'en') as SupportedLanguage;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const choose = (lng: SupportedLanguage) => {
    void i18n.changeLanguage(lng);
    setOpen(false);
    onSelect?.();
  };

  if (variant === 'stacked') {
    return (
      <div className={styles.stacked} role="group" aria-label={t('nav.chooseLanguage')}>
        {supportedLanguages.map((lng) => (
          <button
            key={lng}
            type="button"
            lang={lng}
            onClick={() => choose(lng)}
            className={`${styles.stackedItem} ${lng === current ? styles.stackedActive : ''}`}
            aria-current={lng === current ? 'true' : undefined}
          >
            {languageNames[lng]}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('nav.chooseLanguage')}
      >
        <svg className={styles.globe} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
        </svg>
        <span className={styles.current} lang={current}>{languageNames[current]}</span>
        <svg className={`${styles.caret} ${open ? styles.caretOpen : ''}`} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul className={styles.menu} role="listbox" aria-label={t('nav.chooseLanguage')}>
          {supportedLanguages.map((lng) => (
            <li key={lng}>
              <button
                type="button"
                lang={lng}
                role="option"
                aria-selected={lng === current}
                onClick={() => choose(lng)}
                className={`${styles.option} ${lng === current ? styles.optionActive : ''}`}
              >
                {languageNames[lng]}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
